import type { MetricCategory } from "@/types/tracker";

export const iconPreset = {
  generatedComponentLibrary: "hugeicons",
  appSemanticResolver: "SiteIcon",
  appRegistryAdapter: "lucide-react",
  policy: "Store semantic keys in data and resolve them through the registry.",
} as const;

export const iconography = {
  preset: iconPreset,
  size: {
    inline: "size-4",
    nav: "size-4",
    section: "size-5",
    panel: "size-6",
    feature: "size-8",
  },
  stroke: {
    subtle: 1.75,
    emphasis: 2,
  },
  treatment: {
    mutedClassName: "text-muted-foreground",
    accentClassName: "text-foreground",
    interactiveSurfaceClassName: "bg-accent",
  },
} as const;

export const siteIconKeys = [
  "activity",
  "alertCircle",
  "apple",
  "calendar",
  "clipboardList",
  "droplets",
  "fileBarChart",
  "fileImage",
  "fileText",
  "heart",
  "layers",
  "messageSquare",
  "pill",
  "plus",
  "plusCircle",
  "radio",
  "ruler",
  "scale",
  "settings",
  "stethoscope",
  "thermometer",
  "users",
  "userCog",
  "utensils",
  "wind",
] as const;

export type SiteIconKey = (typeof siteIconKeys)[number];

type ConsoleNavItemId =
  | "pings"
  | "tracker"
  | "treatment-overview"
  | "patients"
  | "appointments"
  | "staff"
  | "communications"
  | "consultation-recordings"
  | "services"
  | "medicine"
  | "nutrition";

type ConsoleNavigationItem = {
  id: ConsoleNavItemId;
  label: string;
  tooltip: string;
  href: string;
  iconKey: SiteIconKey;
};

export const consoleDashboardNavigation = [
  {
    id: "pings",
    label: "Pings",
    tooltip: "Pings",
    href: "/dashboard",
    iconKey: "messageSquare",
  },
  {
    id: "tracker",
    label: "Trackers",
    tooltip: "Trackers",
    href: "/tracker",
    iconKey: "activity",
  },
] as const satisfies readonly ConsoleNavigationItem[];

export const consoleCareNavigation = [
  {
    id: "treatment-overview",
    label: "Treatment Overview",
    tooltip: "Treatment Overview",
    href: "/treatment-overview",
    iconKey: "fileBarChart",
  },
  {
    id: "patients",
    label: "Patients",
    tooltip: "Patients",
    href: "/patients",
    iconKey: "users",
  },
  {
    id: "appointments",
    label: "Appointments",
    tooltip: "Appointments",
    href: "/settings/appointments/calendar",
    iconKey: "calendar",
  },
] as const satisfies readonly ConsoleNavigationItem[];

export const consoleManagementNavigation = [
  {
    id: "staff",
    label: "Staff",
    tooltip: "Staff",
    href: "/settings/staff/view-staff",
    iconKey: "users",
  },
  {
    id: "communications",
    label: "Communications",
    tooltip: "Communications",
    href: "/settings/communications/logs",
    iconKey: "radio",
  },
  {
    id: "consultation-recordings",
    label: "Consultation Recordings",
    tooltip: "Consultation Recordings",
    href: "/settings/consultation-recordings/overview",
    iconKey: "radio",
  },
  {
    id: "services",
    label: "Services",
    tooltip: "Services",
    href: "/settings/services/manage-services",
    iconKey: "layers",
  },
] as const satisfies readonly ConsoleNavigationItem[];

export const consoleDatabaseNavigation = [
  {
    id: "medicine",
    label: "Medicine",
    tooltip: "Medicine",
    href: "/settings/medicine/add-medicine",
    iconKey: "pill",
  },
  {
    id: "nutrition",
    label: "Nutrition",
    tooltip: "Nutrition",
    href: "/settings/nutrition/view-food-items",
    iconKey: "apple",
  },
] as const satisfies readonly ConsoleNavigationItem[];

export type ConsoleSettingsSection = {
  id:
    | "appointments"
    | "medicine"
    | "nutrition"
    | "staff"
    | "communications"
    | "consultation-recordings"
    | "services";
  label: string;
  iconKey: SiteIconKey;
  subSections?: readonly {
    id: string;
    label: string;
  }[];
};

export const consoleSettingsSections = [
  {
    id: "appointments",
    label: "Appointments",
    iconKey: "calendar",
    subSections: [
      { id: "calendar", label: "Calendar" },
      { id: "add-appointment", label: "Add Appointment" },
      { id: "blocked-time", label: "Blocked Time" },
    ],
  },
  {
    id: "medicine",
    label: "Medicine",
    iconKey: "pill",
    subSections: [{ id: "add-medicine", label: "Add New Medicine" }],
  },
  {
    id: "nutrition",
    label: "Nutrition",
    iconKey: "apple",
    subSections: [
      { id: "view-food-items", label: "View Food Items" },
      { id: "serving-units", label: "Serving Units" },
      { id: "add-food-entry", label: "Add Food Entry" },
      { id: "update-food-entry", label: "Edit Food" },
      { id: "food-servings", label: "Food Servings" },
      { id: "food-sources", label: "Food Sources" },
      { id: "food-brands", label: "Food Brands" },
    ],
  },
  {
    id: "staff",
    label: "Staff",
    iconKey: "userCog",
    subSections: [
      { id: "view-staff", label: "View Staff" },
      { id: "create-staff", label: "Add New Staff" },
    ],
  },
  {
    id: "communications",
    label: "Comms",
    iconKey: "radio",
    subSections: [{ id: "logs", label: "Comm Logs" }],
  },
  {
    id: "consultation-recordings",
    label: "Consultation Recordings",
    iconKey: "radio",
    subSections: [{ id: "overview", label: "Overview" }],
  },
  {
    id: "services",
    label: "Services",
    iconKey: "layers",
    subSections: [{ id: "manage-services", label: "Manage Services" }],
  },
] as const satisfies readonly ConsoleSettingsSection[];

export type ConsultationSidebarTab =
  | "overview"
  | "symptoms"
  | "medicines"
  | "reports"
  | "prescription"
  | "nutrition"
  | "treatment-overview";

type ConsultationSidebarItem = {
  id: ConsultationSidebarTab;
  label: string;
  iconKey: SiteIconKey;
};

export const consultationSidebarSections = {
  ehr: [
    { id: "overview", label: "Profile & History", iconKey: "fileText" },
    { id: "symptoms", label: "Vitals & Symptoms", iconKey: "activity" },
    { id: "medicines", label: "Medications", iconKey: "pill" },
    { id: "reports", label: "Investigations", iconKey: "fileImage" },
    {
      id: "treatment-overview",
      label: "Treatment History",
      iconKey: "fileBarChart",
    },
  ] as const satisfies readonly ConsultationSidebarItem[],
  prescription: {
    id: "prescription",
    label: "Prescription",
    iconKey: "clipboardList",
  } as const satisfies ConsultationSidebarItem,
  nutrition: {
    id: "nutrition",
    label: "Nutrition Plan",
    iconKey: "utensils",
  } as const satisfies ConsultationSidebarItem,
} as const;

export type TrackerMetricId =
  | "heart-rate"
  | "blood-pressure"
  | "blood-glucose"
  | "temperature"
  | "respiratory-rate"
  | "hip"
  | "waist-size"
  | "weight";

type TrackerMetricIconToken = {
  label: string;
  unit: string;
  category: MetricCategory;
  iconKey: SiteIconKey;
  iconColorClassName: string;
  iconSurfaceClassName: string;
  cardBorderClassName: string;
  iconColorValue: string;
};

export const trackerMetricOrder = [
  "heart-rate",
  "blood-pressure",
  "blood-glucose",
  "temperature",
  "respiratory-rate",
  "hip",
  "waist-size",
  "weight",
] as const satisfies readonly TrackerMetricId[];

export const trackerMetricIconography = {
  "heart-rate": {
    label: "Heart Rate",
    unit: "bpm",
    category: "heart-health",
    iconKey: "heart",
    iconColorClassName: "text-destructive",
    iconSurfaceClassName: "bg-destructive/10",
    cardBorderClassName: "border-destructive/20 hover:border-destructive/40",
    iconColorValue: "hsl(346 82% 58%)",
  },
  "blood-pressure": {
    label: "Blood Pressure",
    unit: "mmHg",
    category: "heart-health",
    iconKey: "stethoscope",
    iconColorClassName: "text-destructive",
    iconSurfaceClassName: "bg-destructive/10",
    cardBorderClassName: "border-destructive/20 hover:border-destructive/40",
    iconColorValue: "hsl(0 84% 60%)",
  },
  "blood-glucose": {
    label: "Blood Glucose",
    unit: "mg/dL",
    category: "diabetes",
    iconKey: "droplets",
    iconColorClassName: "text-sky-500",
    iconSurfaceClassName: "bg-sky-500/10",
    cardBorderClassName: "border-sky-500/20 hover:border-sky-500/40",
    iconColorValue: "hsl(199 89% 48%)",
  },
  temperature: {
    label: "Temperature",
    unit: "°F",
    category: "all",
    iconKey: "thermometer",
    iconColorClassName: "text-warning",
    iconSurfaceClassName: "bg-warning/10",
    cardBorderClassName: "border-warning/20 hover:border-warning/40",
    iconColorValue: "hsl(24 95% 53%)",
  },
  "respiratory-rate": {
    label: "Respiratory Rate",
    unit: "BPM",
    category: "respiratory-rate",
    iconKey: "wind",
    iconColorClassName: "text-cyan-500",
    iconSurfaceClassName: "bg-cyan-500/10",
    cardBorderClassName: "border-cyan-500/20 hover:border-cyan-500/40",
    iconColorValue: "hsl(189 94% 43%)",
  },
  hip: {
    label: "Hip Measurement",
    unit: "cm",
    category: "hip",
    iconKey: "ruler",
    iconColorClassName: "text-violet-500",
    iconSurfaceClassName: "bg-violet-500/10",
    cardBorderClassName: "border-violet-500/20 hover:border-violet-500/40",
    iconColorValue: "hsl(270 50% 60%)",
  },
  "waist-size": {
    label: "Waist Size",
    unit: "cm",
    category: "waist",
    iconKey: "ruler",
    iconColorClassName: "text-purple-500",
    iconSurfaceClassName: "bg-purple-500/10",
    cardBorderClassName: "border-purple-500/20 hover:border-purple-500/40",
    iconColorValue: "hsl(263 70% 50%)",
  },
  weight: {
    label: "Weight",
    unit: "kg",
    category: "weight",
    iconKey: "scale",
    iconColorClassName: "text-success",
    iconSurfaceClassName: "bg-success/10",
    cardBorderClassName: "border-success/20 hover:border-success/40",
    iconColorValue: "hsl(142 71% 45%)",
  },
} as const satisfies Record<TrackerMetricId, TrackerMetricIconToken>;

export const trackerMetricCategoryLabels: Partial<
  Record<MetricCategory, string>
> = {
  "heart-health": "Heart Health",
  diabetes: "Diabetes",
  all: "General",
  weight: "Weight",
  waist: "Waist",
  hip: "Hip",
  "respiratory-rate": "Respiratory",
};
